import React from "react";

// Club Matches Tab — context line above the form strip / lineup (handoff
// MatchesTabContent.dc.html ctx block, tokens verbatim; brief §0.3):
// {Competition} · {Round} · {Home} {score}–{score} {Away} · {date} · {venue}
// Score in body weight (no big score header). Suffixes only when data
// exists: `(agg 3–6)` / `(4–2 i.E.=pens)` / `n.V.=aet`. Upcoming variant
// without score + TBD chip when undrawn. Optional finest second line:
// referee ONLY when present. Crests inline, club names click targets.
// Empty renders nothing — no '-'/'n/a' (brief §2).
//
// props.ctx (built by the integration from MJ's /api/clubs/<id>/matches/
// contract; club_side already normalises team_reverse):
// { competitionName, round, homeName, homeLogo, homeTeamId,
//   awayName, awayLogo, awayTeamId, scoreText|null (upcoming),
//   suffixText|null, tbd, dateText, venue|null, referee|null }

const Crest = ({ logo, name }) => (
  <span
    style={{
      width: 18,
      height: 18,
      flex: "0 0 18px",
      borderRadius: 4,
      background: "#EEF2F7",
      border: "1px solid #E2E8F0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 7,
      fontWeight: 700,
      color: "#475569",
      overflow: "hidden",
    }}
  >
    {logo ? (
      <img
        src={logo}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      (name || "")
        .split(/\s+/)
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || null
    )}
  </span>
);

const Dot = () => <span style={{ color: "#CBD5E1" }}>·</span>;

const ContextLine = ({ ctx, onTeamClick }) => {
  if (!ctx) return null;
  const teamSpan = (name, logo, teamId, reverse = false) => {
    if (!name) return null;
    const nameEl = (
      <span
        key="n"
        onClick={teamId && onTeamClick ? () => onTeamClick(teamId) : undefined}
        role={teamId ? "button" : undefined}
        style={{
          fontWeight: 600,
          color: "#0F172A",
          cursor: teamId ? "pointer" : "default",
        }}
      >
        {name}
      </span>
    );
    const crestEl = <Crest key="c" logo={logo} name={name} />;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {reverse ? [nameEl, crestEl] : [crestEl, nameEl]}
      </span>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 7,
          fontSize: 13,
          color: "#475569",
        }}
      >
        {ctx.competitionName ? (
          <span style={{ fontWeight: 700, color: "#334155" }}>
            {ctx.competitionName}
          </span>
        ) : null}
        {ctx.round ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#2183FE",
              background: "#EAF3FF",
              borderRadius: 3,
              padding: "3px 6px",
            }}
          >
            {ctx.round}
          </span>
        ) : null}
        <Dot />
        {teamSpan(ctx.homeName, ctx.homeLogo, ctx.homeTeamId)}
        {ctx.scoreText ? (
          <span style={{ fontWeight: 700, color: "#0F172A" }}>
            {ctx.scoreText}
          </span>
        ) : (
          <span style={{ fontWeight: 700, color: "#0F172A" }}>–</span>
        )}
        {teamSpan(ctx.awayName, ctx.awayLogo, ctx.awayTeamId, true)}
        {ctx.suffixText ? (
          <span style={{ color: "#64748B" }}>{ctx.suffixText}</span>
        ) : null}
        {ctx.tbd ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".06em",
              color: "#475569",
              border: "1px solid #E2E8F0",
              background: "#F8FAFC",
              borderRadius: 3,
              padding: "3px 5px",
            }}
          >
            TBD
          </span>
        ) : null}
        {ctx.dateText ? (
          <>
            <Dot />
            <span>{ctx.dateText}</span>
          </>
        ) : null}
        {ctx.venue ? (
          <>
            <Dot />
            <span style={{ color: "#64748B" }}>{ctx.venue}</span>
          </>
        ) : null}
      </div>
      {ctx.referee ? (
        <div style={{ paddingTop: 5, fontSize: 11, color: "#94A3B8" }}>
          {`Referee · ${ctx.referee}`}
        </div>
      ) : null}
    </div>
  );
};

export default ContextLine;
